import { PaperClipOutlined } from '@ant-design/icons';
import { Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationEmail from '~Api/ApplicationEmail/IApplicationEmail';
import { applicationGetAction } from '~Applications/actions';
import Layout from '~Applications/Application/Layout';
import { applicationSelector } from '~Applications/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import ModalStateEnum from '~UI/ModalStateEnum';
import PersonLink from './PersonLink';
import './emails.less';
import { applicationEmailsListAction } from './actions';
import EmailModal from './EmailModal';
import {
    applicationEmailsCountSelector,
    applicationEmailsPageNumberSelector,
    applicationEmailsPageSelector,
} from './selectors';

const defaultPerPage: number = 20;

export default function Emails(): ReactElement {
    const { applicationUuid } = useParams<{ applicationUuid: string }>();

    const [ applicationEmailUuid, setApplicationEmailUuid ] = useState<string>(null);
    const [ emailModalState, setEmailModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const application: IApplication = useSelector((state: IGlobalState): IApplication => applicationSelector(state, applicationUuid));
    const emails: IDictionary<IApplicationEmail> = useSelector((state: IGlobalState): IDictionary<IApplicationEmail> => applicationEmailsPageSelector(state, applicationUuid));
    const currentPage: number = useSelector((state: IGlobalState): number => applicationEmailsPageNumberSelector(state, applicationUuid));
    const totalCount: number = useSelector((state: IGlobalState): number => applicationEmailsCountSelector(state, applicationUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!application) {
            dispatch(applicationGetAction(applicationUuid));
        }
    }, [
        application,
        applicationUuid,
        dispatch,
    ]);

    useEffect((): void => {
        setEmailModalState(ModalStateEnum.Off);
        setApplicationEmailUuid(null);
        dispatch(applicationEmailsListAction(applicationUuid, 1, defaultPerPage));
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onChangeTable: (pagination: TablePaginationConfig) => void = useCallback((pagination: TablePaginationConfig): void => {
        setEmailModalState(ModalStateEnum.Off);
        setApplicationEmailUuid(null);
        dispatch(applicationEmailsListAction(applicationUuid, pagination.current, defaultPerPage));
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onOpenEmailModal: (applicationEmailUuid: string) => void = useCallback((applicationEmailUuid: string): void => {
        setApplicationEmailUuid(applicationEmailUuid);
        setEmailModalState(ModalStateEnum.Open);
    }, []);

    const onCloseEmailModal: () => void = useCallback((): void => {
        setEmailModalState(ModalStateEnum.Closed);
    }, []);

    const columns: ColumnsType<IApplicationEmail> = [
        {
            className: 'sender',
            key: 'sender',
            render: (email: IApplicationEmail): ReactNode => (
                <>
                    <PersonLink
                        email={email.senderEmail}
                        name={email.senderName}
                    />
                    <Typography.Text
                        className='date'
                        type='secondary'
                    >
                        {dayjs(email.sentTime).format('dddd, D MMMM YYYY [at] h:mma')}
                    </Typography.Text>
                </>
            ),
            title: 'From',
            width: 320,
        },
        {
            className: 'subject',
            key: 'subject',
            render: (email: IApplicationEmail): ReactNode => {
                const onClick: () => void = (): void => onOpenEmailModal(email.uuid);

                const attachmentIconBlock: ReactElement = email.attachments.length > 0 && (
                    <PaperClipOutlined />
                );

                const emailModalBlock: ReactElement = (
                    applicationEmailUuid === email.uuid
                    && emailModalState !== ModalStateEnum.Off
                ) && (
                    <EmailModal
                        applicationEmailUuid={email.uuid}
                        isOpen={emailModalState === ModalStateEnum.Open}
                        onCancel={onCloseEmailModal}
                    />
                );

                return (
                    <>
                        <a
                            className='subject'
                            onClick={onClick}
                        >
                            {attachmentIconBlock}
                            {email.subject || '-'}
                        </a>
                        <Typography.Text
                            className='summary'
                            ellipsis={true}
                        >
                            {_.trim(email.summary) || '-'}
                        </Typography.Text>
                        {emailModalBlock}
                    </>
                );
            },
            title: 'Subject',
        },
    ];

    const pagination: TablePaginationConfig = {
        current: currentPage,
        pageSize: defaultPerPage,
        showSizeChanger: false,
        total: totalCount,
    };

    if (!application) {
        return (
            <Layout
                applicationUuid={applicationUuid}
                section='emails'
            >
                <Helmet>
                    <title>Emails</title>
                </Helmet>
                <Typography.Title level={2}>Emails</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    return (
        <Layout
            applicationUuid={applicationUuid}
            section='emails'
        >
            <Helmet>
                <title>Emails - {application.code} - {application.formattedName}</title>
            </Helmet>
            <Typography.Title level={2}>Emails</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(emails)}
                loading={!emails}
                onChange={onChangeTable}
                pagination={pagination}
                rowKey='uuid'
                size='middle'
            />
        </Layout>
    );
}
